import {
  MORPHOLOGY_ORIENTATION_ORDER,
  SPINESIM_MODALITIES,
  MODALITY,
} from "../enums/MedicalImagesEnum";

const getDisplayModalityLabel = instances => {
  if (!Array.isArray(instances) || instances.length === 0) return null;

  const modalities = instances
    .map(
      inst =>
        (inst?.originalModality || inst?.modality || "").toString().toUpperCase()
    )
    .filter(Boolean);

  if (modalities.length === 0) return null;

  const uniqueModalities = [...new Set(modalities)];
  if (
    uniqueModalities.includes(MODALITY.CR) &&
    uniqueModalities.includes(MODALITY.DX)
  ) {
    return "DX/CR";
  }

  return uniqueModalities[0];
};

export const getGroupedThumbnails = (
  selectedTwin,
  instancesByModality = null,
  posturalImages = null,
  painAssessments = null,
  includeVolumeModalities = false
) => {
  if (!selectedTwin) return [];
  const groupedThumbnails = [];
  const { DX = [], CT = [], MR = [] } = instancesByModality;
  const {
    sagittal_dx_instance_id,
    coronal_dx_instance_id,
    ct_instance_id,
    mri_instance_id,
    front_morphology_picture,
    back_morphology_picture,
    left_morphology_picture,
    right_morphology_picture,
  } = selectedTwin;

  // Morphology pictures
  if (posturalImages) {
    const morphFiles = [
      front_morphology_picture,
      back_morphology_picture,
      left_morphology_picture,
      right_morphology_picture,
    ].filter(Boolean);

    const morphInstances = [];
    posturalImages.forEach(img => {
      if (morphFiles.includes(img.title)) {
        morphInstances.push(img);
      }
    });

    morphInstances.sort((a, b) => {
      const aIndex = MORPHOLOGY_ORIENTATION_ORDER.indexOf(a.orientation);
      const bIndex = MORPHOLOGY_ORIENTATION_ORDER.indexOf(b.orientation);

      if (aIndex >= 0 && bIndex >= 0) {
        return aIndex - bIndex;
      } else if (aIndex >= 0) return -1;
      else if (bIndex >= 0) return 1;
      return 0;
    });

    if (morphInstances.length > 0) {
      groupedThumbnails.push({
        modality: SPINESIM_MODALITIES.POSTURE,
        instances: morphInstances,
        _id: SPINESIM_MODALITIES.POSTURE,
        url: morphInstances[0].url,
        title: SPINESIM_MODALITIES.POSTURE,
        date: morphInstances[0].date,
        count: morphInstances.length,
      });
    }
  }

  // DX modality - combine sagittal and coronal
  const dxInstances = [];
  [sagittal_dx_instance_id, coronal_dx_instance_id].forEach(id => {
    const inst = DX.find(i => i._id === id);
    if (inst) {
      dxInstances.push(inst);
    }
  });

  if (dxInstances.length > 0) {
    const dxDisplayModality =
      getDisplayModalityLabel(dxInstances) || SPINESIM_MODALITIES.DX;

    // Create one thumbnail for all DX instances
    groupedThumbnails.push({
      modality: SPINESIM_MODALITIES.DX,
      displayModality: dxDisplayModality,
      instances: dxInstances,
      _id: SPINESIM_MODALITIES.DX,
      url: dxInstances[0].url,
      title: dxDisplayModality,
      date: dxInstances[0].date || dxInstances[0].creation_date,
      count: dxInstances.length,
    });
  }

  // CT modality
  if (ct_instance_id) {
    const inst = CT.find(i => i._id === ct_instance_id);
    if (inst) {
      const ctDisplayModality =
        (inst.originalModality ||
          inst.modality ||
          SPINESIM_MODALITIES.CT).toString().toUpperCase();
      groupedThumbnails.push({
        modality: SPINESIM_MODALITIES.CT,
        displayModality: ctDisplayModality,
        instances: [inst],
        _id: SPINESIM_MODALITIES.CT,
        url: inst.url,
        title: ctDisplayModality,
        date: inst.date || inst.creation_date,
        count: 1,
      });

      // Add CT Volume modality if requested
      if (includeVolumeModalities) {
        groupedThumbnails.push({
          modality: SPINESIM_MODALITIES.CT_VOLUME,
          displayModality: ctDisplayModality,
          instances: [inst], // Same instance, different modality for filtering
          _id: SPINESIM_MODALITIES.CT_VOLUME,
          url: inst.url,
          title: SPINESIM_MODALITIES.CT_VOLUME,
          date: inst.date || inst.creation_date,
          count: 1,
        });
      }
    }
  }

  // MR modality
  if (mri_instance_id) {
    const inst = MR.find(i => i._id === mri_instance_id);
    if (inst) {
      const mrDisplayModality =
        (inst.originalModality ||
          inst.modality ||
          SPINESIM_MODALITIES.MR).toString().toUpperCase();
      groupedThumbnails.push({
        modality: SPINESIM_MODALITIES.MR,
        displayModality: mrDisplayModality,
        instances: [inst],
        _id: SPINESIM_MODALITIES.MR,
        url: inst.url,
        title: mrDisplayModality,
        date: inst.date || inst.creation_date,
        count: 1,
      });

      // Add MR Volume modality if requested
      if (includeVolumeModalities) {
        groupedThumbnails.push({
          modality: SPINESIM_MODALITIES.MR_VOLUME,
          displayModality: mrDisplayModality,
          instances: [inst], // Same instance, different modality for filtering
          _id: SPINESIM_MODALITIES.MR_VOLUME,
          url: inst.url,
          title: SPINESIM_MODALITIES.MR_VOLUME,
          date: inst.date || inst.creation_date,
          count: 1,
        });
      }
    }
  }

  if (selectedTwin.pain_assessment && painAssessments) {
    const painAssessment = painAssessments.find(
      p => p._id === selectedTwin.pain_assessment
    );
    if (painAssessment) {
      groupedThumbnails.push({
        modality: SPINESIM_MODALITIES.PAIN,
        instances: [painAssessment],
        _id: SPINESIM_MODALITIES.PAIN,
        title: SPINESIM_MODALITIES.PAIN,
        date: painAssessment.date || painAssessment.createdAt,
        count: 1,
      });
    }
  }

  return groupedThumbnails;
};
